import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FORM_FIELD_VALIDATORS_BY_TYPE } from "@tellescope/validation";
import { AnswerForType } from "@tellescope/types-models";
import {
  convertFormFieldType,
  getRequiredAddressKeys,
  isBookingBlockedByEligibility,
  isDateWithinOffsetBounds,
  isValidZipCode,
  isValueEmpty,
} from "../../utils";
import { isEmptyRichTextValue, validateFieldByType } from "../validate-field-by-type";
import { getValueForValidation } from "./get-value-for-validation";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface ResolveFieldValidityArgs {
  field: TellescopeModelFromName<"form_fields">;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  pendingFiles: Record<string, File[]>;
  isSubFieldHidden: (fieldId: string) => boolean;
}

export interface FieldValueValidity {
  valid: boolean;
  error?: string;
}

const VALID: FieldValueValidity = { valid: true };
const INVALID: FieldValueValidity = { valid: false };

const optionalIs = (isOptional: boolean | undefined): FieldValueValidity => ({ valid: !!isOptional });

export const resolveFieldValidity = ({
  field,
  fieldsMap,
  currentResponses,
  pendingFiles,
  isSubFieldHidden,
}: ResolveFieldValidityArgs): FieldValueValidity => {
  const fieldId = field.id;
  const response = currentResponses[fieldId];

  if (convertFormFieldType(field.type) === null) return VALID;

  if (field.type === "Bridge Eligibility" || field.type === "Candid Eligibility") return VALID;

  if (field.type === "Hidden Value") return VALID;

  if (response?.disabled) return VALID;

  if (isBookingBlockedByEligibility(field, Object.values(currentResponses))) return VALID;

  if (field.type === "Question Group") {
    return {
      valid: (field.options?.subFields ?? []).every((sf) => {
        const subField = fieldsMap?.[sf.id];
        if (!subField || subField.isOptional) return true;
        if (isSubFieldHidden(sf.id)) return true;
        return !isValueEmpty(currentResponses[sf.id]?.answer.value);
      }),
    };
  }

  if (field.type === "string" || field.type === "stringLong") {
    const raw = response?.answer?.value;
    const trimmed = typeof raw === "string" ? raw.trim() : raw;
    return isValueEmpty(trimmed)
      ? optionalIs(field.isOptional)
      : { valid: validateFieldByType(field, trimmed) === null };
  }

  if (field.type === "dateString") {
    const strValue = response?.answer?.value;
    if (typeof strValue !== "string" || !strValue) return optionalIs(field.isOptional);
    const [monthStr, dayStr, yearStr] = strValue.split("-");
    const month = parseInt(monthStr ?? "");
    const day = parseInt(dayStr ?? "");
    const year = parseInt(yearStr ?? "");
    if (isNaN(month) || isNaN(day) || isNaN(year)) return INVALID;
    const parsed = new Date(year, month - 1, day);
    parsed.setHours(0, 0, 0, 0);
    return {
      valid: isDateWithinOffsetBounds(parsed, field.options, {
        normalizeMinToMidnight: true,
        maxBoundary: "midnight",
      }).withinBounds,
    };
  }

  if (field.type === "date") {
    const dateValue = response?.answer?.value;
    if (!(dateValue instanceof Date)) return optionalIs(field.isOptional);
    const normalizedValue = new Date(dateValue);
    normalizedValue.setHours(0, 0, 0, 0);
    return {
      valid: isDateWithinOffsetBounds(normalizedValue, field.options, {
        normalizeMinToMidnight: true,
        maxBoundary: "midnight",
      }).withinBounds,
    };
  }

  if (field.type === "multiple_choice" || field.type === "Dropdown") {
    const selectedOptions = Array.isArray(response?.answer.value) ? (response.answer.value as string[]) : [];
    if (selectedOptions.length === 0) return optionalIs(field.isOptional);
    return { valid: validateFieldByType(field, selectedOptions) === null };
  }

  if (field.type === "Height") {
    const value = response?.answer?.value;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    return { valid: validateFieldByType(field, value) === null };
  }

  if (field.type === "Medications") {
    const value = response?.answer?.value;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    return { valid: validateFieldByType(field, value) === null };
  }

  if (field.type === "Insurance") {
    const value = response?.answer?.value;
    // Type errors are checked before emptiness here, unlike the neighbouring cases: a malformed
    // insurance object is invalid even when the field is optional.
    if (validateFieldByType(field, value)) return INVALID;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    return VALID;
  }

  if (field.type === "Related Contacts") {
    const value = response?.answer?.value;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    return { valid: validateFieldByType(field, value) === null };
  }

  if (field.type === "Address") {
    if (!response) return optionalIs(field.isOptional);
    const value = response.answer?.value;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    if (typeof value !== "object" || value === null) return optionalIs(field.isOptional);

    // Reflect.get rather than a spread: reads must resolve through the prototype chain, as the
    // original index-through-a-cast did. A spread would only see own enumerable keys.
    const requiredKeys = getRequiredAddressKeys(field.options);

    if (
      !requiredKeys.every((key) => {
        const val = Reflect.get(value, key);
        return typeof val === "string" && val.trim().length > 0;
      })
    )
      return INVALID;

    if (requiredKeys.includes("zipCode")) {
      const zipCode = Reflect.get(value, "zipCode");
      if (typeof zipCode === "string") return { valid: isValidZipCode(zipCode, { fullZIP: field.fullZIP }) };
    }

    return VALID;
  }

  if (field.type === "file" || field.type === "files") {
    const storedValue = response?.answer?.value;
    const storedCount = Array.isArray(storedValue) ? storedValue.length : isValueEmpty(storedValue) ? 0 : 1;
    const totalCount = storedCount + (pendingFiles[fieldId]?.length ?? 0);
    if (totalCount === 0) return optionalIs(field.isOptional);
    const { min, max } = field.options ?? {};
    if (min !== undefined && totalCount < min) return INVALID;
    if (max !== undefined && totalCount > max) return INVALID;
    return VALID;
  }

  if (field.type === "Table Input") {
    const value = response?.answer?.value;
    if (isValueEmpty(value)) return optionalIs(field.isOptional);
    return { valid: validateFieldByType(field, value) === null };
  }

  if (field.type === "Rich Text" && isEmptyRichTextValue(response?.answer.value)) return optionalIs(field.isOptional);

  if (isValueEmpty(response?.answer.value)) return optionalIs(field.isOptional);

  if (field.type === "signature") {
    const answerValue = response?.answer?.value;
    if (typeof answerValue === "object" && answerValue !== null && "signed" in answerValue && !answerValue.signed) {
      return INVALID;
    }
  }

  const validator = FORM_FIELD_VALIDATORS_BY_TYPE[field.type];
  if (validator) {
    const validationType = convertFormFieldType(field.type);
    const valueForValidator =
      validationType && validationType !== "Question Group"
        ? getValueForValidation<keyof AnswerForType>(validationType, response?.answer.value)
        : response?.answer.value;
    try {
      return { valid: !!validator(valueForValidator, field.options, field.isOptional) };
    } catch (err) {
      if (typeof err === "object" && err !== null && "message" in err) {
        return { valid: false, error: String(err.message) };
      }
      return INVALID;
    }
  }

  if (!response || isValueEmpty(response.answer?.value)) return INVALID;

  return VALID;
};
